import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import ContentLayout from '../ContentLayout';

const ACCOUNT_NAV = [
  {
    to: '/account/profile',
    labelKey: 'account.profile',
    descriptionKey: 'account.profileDescription',
    contentWidth: 'narrow',
  },
  {
    to: '/account/preferences',
    labelKey: 'account.preferences',
    descriptionKey: 'account.preferencesDescription',
    contentWidth: 'narrow',
  },
  {
    to: '/account/security',
    labelKey: 'account.security',
    descriptionKey: 'account.securityDescription',
    contentWidth: 'narrow',
  },
  {
    to: '/account/sessions',
    labelKey: 'account.sessions',
    descriptionKey: 'account.sessionsDescription',
    contentWidth: 'wide',
  },
  {
    to: '/account/public-links',
    labelKey: 'account.publicLinks',
    descriptionKey: 'account.publicLinksDescription',
    contentWidth: 'wide',
  },
] as const;

const ACCOUNT_CONTENT_MAX_WIDTH = {
  narrow: 'max-w-2xl',
  wide: 'max-w-4xl',
} as const;

/** Slug (`profile`) → tab label i18n key (`account.profile`). */
const accountTitleKeyBySlug: ReadonlyMap<string, string> = new Map(
  ACCOUNT_NAV.map((item) => [item.to.slice('/account/'.length), item.labelKey] as const),
);

/** Title i18n key for an account route slug, or undefined if unknown. */
export function getAccountTitleKeyBySlug(slug: string): string | undefined {
  return accountTitleKeyBySlug.get(slug);
}

/**
 * Account hub shell: Tabs in ContentLayout.Header (same min-h-20 chrome as other routes)
 * + active-tab description + routed body in a left-aligned max-width column.
 * Tabs + bordered ContentLayout stay full width; description + Outlet share the same column.
 */
function AccountLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const activeItem =
    ACCOUNT_NAV.find((item) => location.pathname.startsWith(item.to)) ?? ACCOUNT_NAV[0];
  const contentMaxWidthClass = ACCOUNT_CONTENT_MAX_WIDTH[activeItem.contentWidth];

  const items: TabsProps['items'] = useMemo(
    () =>
      ACCOUNT_NAV.map((item) => ({
        key: item.to,
        // Link keeps navigation reliable in tests/jsdom; onChange covers keyboard tab switches.
        label: (
          <Link to={item.to} className="!text-inherit no-underline">
            {t(item.labelKey)}
          </Link>
        ),
      })),
    [t],
  );

  return (
    <ContentLayout scrollable={false}>
      <ContentLayout.Header>
        {/* Mirror FilesHeader/UsersHeader: gap-1 column inside min-h-20 sticky header */}
        <div className="relative flex w-full min-w-0 flex-col justify-end gap-1">
          <nav aria-label={t('account.navAria')} className="min-w-0 overflow-x-hidden">
            {/* Route sync: Tabs navigate; panes stay empty — content is <Outlet /> */}
            <Tabs
              activeKey={activeItem.to}
              className="dark:[&_.ant-tabs-tab]:!text-gray-dark dark:[&_.ant-tabs-tab:hover]:!text-blue-dark w-full min-w-0 [&_.ant-tabs-content-holder]:hidden dark:[&_.ant-tabs-ink-bar]:!bg-black [&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-nav]:w-full [&_.ant-tabs-nav]:min-w-0 dark:[&_.ant-tabs-tab_a]:!text-inherit dark:[&_.ant-tabs-tab-active]:!text-black dark:[&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-black"
              items={items}
              onChange={(key) => {
                void navigate(key);
              }}
              tabPlacement="top"
            />
          </nav>
        </div>
      </ContentLayout.Header>
      <ContentLayout.Content>
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-x-hidden px-4 pt-4 pb-4 md:px-8">
          {/* Left-aligned max-width band: description + Outlet share one column (shell stays full width) */}
          <section
            className={clsx(
              'flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto',
              contentMaxWidthClass,
            )}
          >
            <p className="text-gray-dark mb-4 shrink-0 text-left text-sm">
              {t(activeItem.descriptionKey)}
            </p>
            <Outlet />
          </section>
        </div>
      </ContentLayout.Content>
    </ContentLayout>
  );
}

export default AccountLayout;
export { ACCOUNT_CONTENT_MAX_WIDTH, ACCOUNT_NAV };
